import * as React from 'react'

import apis from '@app/api/api'
import useI18n from '@store/i18n/useI18n'
import Logger from '@utils/Logger'
import theme from '@theme/Theme'

import styled from '@theme/styled-components'
import Searchbar from '@components/searchbar/searchbar'
import Button from '@components/button/button'
import Loader from 'react-loader-spinner'
import TextInput from '@components/textInput/textInput'

import { motion, container } from '@utils/Motion'
import AccentSanitizer from '../../../utils/AccentSanitizer'
import Swal from 'sweetalert2'
import ScreenContainer from '@components/screenContainer/screenContainer'
import DynamicList from '@components/infiniteList/dynamicList'

import { AdminCompaniesGroup } from '@components/listItem/adminCompaniesGroup'

import { Formik } from 'formik'
import MultiSelect from '@components/select/multiSelect'

interface companyGroupFormik {
  name: string
  companies: Option[]
}

const NEW_GROUP: companyGroupFormik = {
  name: '',
  companies: [],
}

interface Props {
  navigation: Navigation
}

const sanitizer = new AccentSanitizer() // gestion de la casse

const AdminCompanyGroup = ({ navigation }: Props) => {
  const i18n = useI18n()

  const [screenStatus, setScreenStatus] = React.useState<'fetched' | 'loading' | 'error'>('loading')
  const [text, setText] = React.useState<string>('')
  const [dirtyForm, setDirtyForm] = React.useState<boolean>(false) // formulaire en cours d'édition ou de création
  const [formType, setFormType] = React.useState<undefined | 'editing' | 'adding'>('editing')
  const [listCompanies, setListCompanies] = React.useState<AdminCompanies[]>([])
  const [listCompanyGroups, setListCompanyGroups] = React.useState<CompanyGroup[]>([])
  const [selectedGroup, setSelectedGroup] = React.useState<companyGroupFormik>()
  const [currentGroupId, setCurrentGroupId] = React.useState<string>()
  const [companiesAlreadyInAGroup, setCompaniesAlreadyInAGroup] = React.useState<CompanyGroupCompany[]>([])
  const [openedTab, setOpenedTab] = React.useState<boolean[]>([])

  React.useEffect(() => {
    fetchGroups()
  }, [])

  // Charge les listes des regroupements et des entreprises
  const fetchGroups = () =>
    Promise.all([apis.admin.list.companies(), apis.admin.companyGroup.get()])
      .then(([fetchedCompanies, fetchedCompagnyGroups]) => {
        setListCompanies([fetchedCompanies])
        setListCompanyGroups(fetchedCompagnyGroups.companyGroups)
        setScreenStatus('fetched')
        setOpenedTab(new Array(fetchedCompagnyGroups.companyGroups.length).fill(true))
      })
      .catch((err) => {
        Logger.info('ERROR FETCHING ADMIN COMPANIES AND GROUPS : ', err)
        setScreenStatus('error')
      })

  const toggleOpenedState = (idx: number) => {
    setOpenedTab((opentabs) => opentabs.map((v, i) => (i === idx ? !v : v)))
  }

  // Recharge la liste des regroupements
  const reloadCompanyGroup = () => {
    apis.admin.companyGroup
      .get()
      .then((fetchedCompagnyGroups) => {
        setListCompanyGroups(fetchedCompagnyGroups.companyGroups)
      })
      .catch((err) => {
        Logger.info('ERROR FETCHING ADMIN COMPANIES AND GROUPS : ', err)
        setScreenStatus('error')
      })
  }

  // Ajout ou modification d'un regroupement
  const addCompanyGroup = (values: companyGroupFormik) => {
    if (formType === 'adding') {
      apis.admin.companyGroup
        .add({ name: values.name, companies: values.companies.map((company) => company.value) })
        .then(() => {
          Swal.fire({
            title: i18n.t(`components.adminCompanies.group.alertAdding.success.title`),
            text: i18n.t(`components.adminCompanies.group.alertAdding.success.text`, { name: values.name }),
            icon: 'success',
          })
          setSelectedGroup(undefined)
          setCurrentGroupId(undefined)
          setDirtyForm(false)
          setCompaniesAlreadyInAGroup([])
          reloadCompanyGroup()
        })
        .catch((err) => {
          Swal.fire({
            title: i18n.t(`components.adminCompanies.group.alertAdding.error.title`),
            text: i18n.t(`components.adminCompanies.group.alertAdding.error.text`, { name: values.name }),
            icon: 'error',
          })
          Logger.error('Unable to load company groups', err)
        })
    } else if (formType === 'editing' && !!currentGroupId) {
      apis.admin.companyGroup
        .edit(currentGroupId, { name: values.name, companies: values.companies.map((company) => company.value) })
        .then((test) => {
          Swal.fire({
            title: i18n.t(`components.adminCompanies.group.alertEditing.success.title`),
            text: i18n.t(`components.adminCompanies.group.alertEditing.success.text`, { name: values.name }),
            icon: 'success',
          })
          setDirtyForm(false)
          setCompaniesAlreadyInAGroup([])
          reloadCompanyGroup()
        })
        .catch((err) => {
          Swal.fire({
            title: i18n.t(`components.adminCompanies.group.alertEditing.error.title`),
            text: i18n.t(`components.adminCompanies.group.alertEditing.error.text`, { name: values.name }),
            icon: 'error',
          })
          Logger.error('Unable to load company groups', err)
        })
    }
  }

  const sanitize = (text: string) => {
    return sanitizer.sanitize(text.trim())
  }

  const setFormField = (callback: () => void) => {
    callback()
  }

  const alertCancellingEdition = (callback?: () => void) => {
    if (dirtyForm) {
      Swal.fire({
        title: i18n.t('components.adminCompanies.alertCancelling.title'),
        text: i18n.t('components.adminCompanies.alertCancelling.text'),
        showCancelButton: true,
        confirmButtonText: i18n.t('components.adminCompanies.alertCancelling.confirm'),
        confirmButtonColor: 'green',
        cancelButtonText: i18n.t('components.adminCompanies.alertCancelling.cancel'),
        cancelButtonColor: 'red',
      }).then((result) => {
        if (result.isConfirmed) {
          setSelectedGroup(undefined)
          setCurrentGroupId(undefined)
          setDirtyForm(false)
          !!callback && callback()
        }
      })
    } else {
      setDirtyForm(false)
      !!callback && callback()
    }
  }

  // Pour l'édition, vérifie que au moins un champ a été modifié
  const valuesHasChanged = (values: companyGroupFormik) => {
    return values !== selectedGroup
  }

  const includesCompany = (companies: CompanyGroupCompany[], text: string) => {
    const sanitizeText = sanitize(text)
    return companies.find((company) => sanitize(company.name).includes(sanitizeText)) || false
  }

  const searchedGroups: CompanyGroup[] = React.useMemo(() => {
    const sanitizeText = sanitize(text)
    return text
      ? listCompanyGroups.filter(
          (group) => sanitize(group.name).includes(sanitizeText) || includesCompany(group.companies, sanitizeText)
        )
      : (listCompanyGroups as CompanyGroup[])
  }, [listCompanyGroups, text])

  const sortedGroups: CompanyGroup[] = React.useMemo(
    () => searchedGroups.sort((a, b) => a.name.localeCompare(b.name)),
    [searchedGroups, listCompanyGroups]
  )

  return (
    <ScreenContainer
      menu={
        <LeftContainer>
          <ScreenTitleContainer>
            <ScreenTitle>{i18n.t('components.adminCompanies.screenTitle')}</ScreenTitle>
            <Back onClick={() => navigation.push('/')}>{i18n.t('components.menu.back')}</Back>
          </ScreenTitleContainer>
          <Legend>
            <LeftButton
              onClick={() => {
                navigation.navigate('/admin/companies')
              }}>
              {i18n.t('components.adminCompanies.level.companies')}
            </LeftButton>
            <RigthButton>{i18n.t('components.adminCompanies.level.group')}</RigthButton>
          </Legend>
          {screenStatus === 'loading' ? (
            <StatusContainer>
              <Loader type="ThreeDots" color={theme.colors.classicButtonLoader} height={30} />
            </StatusContainer>
          ) : screenStatus === 'error' ? (
            <ErrorLibelle>{i18n.t('components.adminCompanies.error')}</ErrorLibelle>
          ) : (
            <>
              <Searchbar
                placeholder={i18n.t('components.adminCompanies.group.searchbar')}
                text={text}
                setText={setText}
              />
              <CompanyList
                as={motion.div}
                className="container"
                variants={container}
                initial="hidden"
                animate="visible">
                {sortedGroups.length === 0 ? (
                  <NoItemContainer>
                    <NoItemLibelle>{i18n.t('components.adminCompanies.noItem')}</NoItemLibelle>
                  </NoItemContainer>
                ) : (
                  <DynamicList
                    data={sortedGroups}
                    renderItem={(c, idx) => (
                      <AdminCompaniesGroup
                        companyGroup={c}
                        searchText={text}
                        onClick={() =>
                          alertCancellingEdition(() => {
                            const company = listCompanyGroups.filter((group) => group.id === c.id)[0]
                            setFormType('editing')
                            setCurrentGroupId(c.id)
                            setSelectedGroup({
                              name: company.name,
                              companies: company.companies.map((company) => {
                                return { value: company.id, label: company.name }
                              }),
                            })
                          })
                        }
                        isOpened={openedTab[idx]}
                        toggleOpenedState={() => toggleOpenedState(idx)}
                      />
                    )}
                    openedTab={openedTab}
                  />
                )}
              </CompanyList>
              <ButtonContainer>
                <Button
                  title={i18n.t(`components.adminCompanies.group.add`)}
                  status="active"
                  type="navigation"
                  size="medium"
                  onClick={() =>
                    alertCancellingEdition(() => {
                      setCurrentGroupId(undefined)
                      setSelectedGroup(NEW_GROUP)
                      setFormType('adding')
                    })
                  }
                  fitContent
                  icon="add"
                />
              </ButtonContainer>
            </>
          )}
        </LeftContainer>
      }
      content={
        <RightContainer>
          {!!selectedGroup ? (
            <Formik
              initialValues={selectedGroup as companyGroupFormik}
              onSubmit={(companyGroupInput: companyGroupFormik) => addCompanyGroup(companyGroupInput)}>
              {({ values, setValues, setFieldValue, submitForm }) => {
                React.useEffect(() => {
                  setValues(selectedGroup)
                }, [selectedGroup])

                React.useEffect(() => {
                  if (valuesHasChanged(values)) {
                    setDirtyForm(true)
                  }
                }, [values])

                React.useEffect(() => {
                  // Liste les entreprises qui sont déjà dans un regroupement
                  listCompanyGroups.forEach((group) => {
                    group.companies.forEach((company) => {
                      if (companiesAlreadyInAGroup.indexOf(company) < 0) {
                        companiesAlreadyInAGroup.push(company)
                      }
                    })
                  })
                }, [selectedGroup])

                // Vérifie si une entreprises est déjà dans un autre regroupement
                const isInACompanyGroup = (company: string) => {
                  return (
                    companiesAlreadyInAGroup.some((companyExisting) => company === companyExisting.id) &&
                    !selectedGroup.companies.some((companyExisting) => company === companyExisting.value)
                  )
                }

                return (
                  <FormContainer>
                    <SectionTitleContainer>{i18n.t('components.adminCompanies.group.section01')}</SectionTitleContainer>{' '}
                    <RowContainer>
                      <FormItemContainer>
                        <TextInput
                          title={i18n.t('components.adminCompanies.group.item01')}
                          value={values.name}
                          onChange={(name) => setFieldValue('name', name, true)}
                          placeholder={i18n.t('components.adminCompanies.group.placeholder01')}
                          error={
                            !values.name
                              ? i18n.t('components.adminCompanies.group.errors.name')
                              : listCompanyGroups.find(
                                  (group) =>
                                    sanitize(values.name) === sanitize(group.name) && group.id !== currentGroupId
                                )
                              ? i18n.t('components.adminCompanies.group.alertAdding.wrongName.title')
                              : undefined
                          }
                        />
                      </FormItemContainer>
                    </RowContainer>
                    <RowContainer>
                      <FormItemContainer>
                        <SelectorContainer>
                          <SelectorTitle>{i18n.t('components.adminCompanies.group.item02')}</SelectorTitle>
                          <MultiSelect
                            options={listCompanies[0].companies.map((c) => ({ value: c.id, label: c.libelle }))}
                            selected={values.companies}
                            setSelected={(selected) => setFormField(() => setFieldValue('companies', selected, true))}
                            isClearable
                            placeholder={i18n.t('components.adminCompanies.group.placeholder02')}
                            noMargin
                            error={
                              values.companies.length < 1
                                ? i18n.t('components.adminCompanies.group.errors.company')
                                : undefined
                            }
                            isOptionDisabled={(option) => isInACompanyGroup(option.id)}
                            closeMenuOnSelect={false}
                          />
                          <Note>{i18n.t('components.adminCompanies.group.note')}</Note>
                        </SelectorContainer>
                      </FormItemContainer>
                    </RowContainer>
                    <FormButtonContainer>
                      <Button
                        title={i18n.t('components.adminCompanies.cancel')}
                        status={
                          valuesHasChanged(values) && (values.companies?.length > 0 || !!values.name)
                            ? 'active'
                            : 'disabled'
                        }
                        type="classic"
                        size="medium"
                        onClick={() => alertCancellingEdition()}
                        icon="bin"
                      />
                      <Button
                        title={
                          formType === 'adding'
                            ? i18n.t('components.adminCompanies.addSaveItem')
                            : i18n.t('components.adminCompanies.saveItem')
                        }
                        status={
                          valuesHasChanged(values) && !!values.name && values.companies?.length ? 'active' : 'disabled'
                        }
                        type="navigation"
                        size="medium"
                        onClick={submitForm}
                        icon="add"
                      />
                    </FormButtonContainer>
                  </FormContainer>
                )
              }}
            </Formik>
          ) : (
            <NoSelectedContainer>
              <NoSelectedLibelle>{i18n.t('components.adminCompanies.group.noSelected')}</NoSelectedLibelle>
            </NoSelectedContainer>
          )}
        </RightContainer>
      }
    />
  )
}

export default AdminCompanyGroup

const LeftContainer = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px 10px 0 10px;
  overflow: hidden;
`
const StatusContainer = styled('div')`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 100%;
`
const ErrorLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
  margin: 40px;
`
const Back = styled('div')`
  ${(props) => props.theme.fonts.backLibelle};
  cursor: pointer;
`
const ScreenTitle = styled('div')`
  ${(props) => props.theme.fonts.title};
`
const ScreenTitleContainer = styled('div')`
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 10px;
  margin: 0 10px 15px 10px;
`
const CompanyList = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow: scroll;
  padding-bottom: 75px;
`

const ButtonContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  bottom: 0;
  height: 75px;
  width: 450px;
  background: linear-gradient(359.87deg, #ffffff 10%, rgba(255, 255, 255, 0) 106.99%);
`
const RightContainer = styled('div')`
  flex: 1;
  display: flex;
  background-color: ${(props) => props.theme.colors.filterBackgroundColor};
`
const FormContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin: 50px;
  justify-content: flex-start;
  overflow: scroll;
`
const NoSelectedContainer = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`
const NoSelectedLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
`
const NoItemLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
`
const SectionTitleContainer = styled('div')`
  font-family: Avenir;
  font-style: normal;
  font-weight: 900;
  font-size: 20px;
  line-height: 24px;
  color: #0053a1;
  margin: 10px;
  margin-top: 0;
`
const RowContainer = styled('div')`
  display: flex;
  margin: 10px;
  margin-top: 0;
`

const FormItemContainer = styled('div')`
  display: flex;
  flex: 1;
  margin: 10px;
  margin-top: 0;
`
const FormButtonContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-around;
  position: absolute;
  bottom: 25px;
  right: 25px;
  min-width: 600px;
`
const SelectorContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;

  margin: 5px;
`
const SelectorTitle = styled('div')`
  font-family: Avenir;
  font-style: normal;
  font-weight: 900;
  font-size: 14px;
  line-height: 19px;
  color: #9494a2;
`

const Legend = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  margin-bottom: 20px;
  border-radius: 7px;
  background-color: ${(props) => props.theme.colors.greyBackground};
`

const LeftButton = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  border-radius: 7px;
  color: ${(props) => props.theme.colors.secondary_grey};
  background-color: ${(props) => props.theme.colors.greyBackground};
`
const RigthButton = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  border-radius: 7px;
  color: ${(props) => props.theme.colors.secondary_text};
  background-color: ${(props) => props.theme.colors.primary_blue};
`
const NoItemContainer = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`
const Note = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  margin-top: 5px;
  font-style: italic;
`
